import { create } from "zustand";
import { useEffect, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

import type { Role, User } from "./api/types";
import { fetchOidcSession, oidcLogout, oidcLogoutRedirect, setSessionBearer } from "./api/client";

interface AuthState {
  user: User | null;
  /**
   * False until the one-time OIDC session bootstrap (`/auth/me`) has run, so the
   * router can hold rendering instead of flashing the login screen for an
   * already-authenticated operator. Always true in the dev-stub path.
   */
  bootstrapped: boolean;
  /**
   * Dev-only stub login. Tree-shaken out of production builds: the body is
   * guarded by `import.meta.env.DEV`, so in `pnpm build` this is a no-op.
   * Real flow uses oidcLoginRedirect() in client.ts.
   */
  signInStub(): void;
  /**
   * Resolve the current operator from the server-side OIDC session: in DEV a
   * dev token skips OIDC; otherwise we call `GET /auth/me`, and on 200 keep the
   * returned access_token as the in-memory SPA Bearer + set the user from
   * sub/email/roles. On 401 the user stays null (Login is shown). Idempotent.
   */
  bootstrap(): Promise<void>;
  /**
   * Clear the operator session.
   *  - default (`reason: "user"`): explicit "Sign out" — in the real OIDC flow
   *    this does the RP-Initiated Logout full-page redirect so identity ends the
   *    IdP session fleet-wide.
   *  - `reason: "unauthorized"`: a 401 handler — clear LOCAL state ONLY and let
   *    the caller navigate once to /login. Must NOT fire the RP-initiated logout
   *    redirect, which would race that navigation and, on a fan-out of concurrent
   *    401s, redirect repeatedly. (W1)
   */
  logout(opts?: { reason?: "user" | "unauthorized" }): void;
}

const STUB_USER: User = {
  id: "usr_demo",
  email: "operator@proximi.io",
  role: "admin",
  organizationId: "org_demo",
  organizationSlug: "proximi",
};

/**
 * True when a dev token is available (env or stored), bypassing the OIDC flow.
 * DEV builds only. Production always asks `GET /auth/me`: up to v0.1.7 this
 * also fired in production, where the bootstrap itself had stored the session
 * token under `aether:token`, so every later page load skipped `/auth/me`, left
 * `user` null and bounced to the Sign in card (the sign-in loop).
 */
function hasDevToken(): boolean {
  if (!import.meta.env.DEV) return false;
  try {
    if (typeof localStorage !== "undefined" && localStorage.getItem("aether:token")) return true;
  } catch {
    // ignore
  }
  return Boolean(import.meta.env.VITE_DEV_TOKEN);
}

/** Map the OIDC roles claim to the SPA's coarse role model. */
function roleFromClaims(roles: string[]): Role {
  if (roles.includes("admin")) return "admin";
  if (roles.includes("client")) return "client";
  return "viewer";
}

export const useAuthStore = create<AuthState>((set, get) => ({
  // The localStorage "aether:session" flag is a dev-only affordance used by the
  // stub login; only honour it in dev so a prod bundle never trusts a
  // client-set boolean. Production authority comes from the OIDC session.
  user: (() => {
    if (!import.meta.env.DEV) return null;
    try {
      return localStorage.getItem("aether:session") ? STUB_USER : null;
    } catch {
      return null;
    }
  })(),
  // Start un-bootstrapped so production waits for `/auth/me`. In dev with the
  // stub session already set, treat it as bootstrapped to keep the demo path.
  bootstrapped: import.meta.env.DEV
    ? (() => {
        try {
          return Boolean(localStorage.getItem("aether:session"));
        } catch {
          return false;
        }
      })()
    : false,
  signInStub: () => {
    if (!import.meta.env.DEV) return;
    try {
      localStorage.setItem("aether:session", "1");
    } catch {
      // ignore
    }
    set({ user: STUB_USER, bootstrapped: true });
  },
  bootstrap: async () => {
    if (get().bootstrapped) return;
    // DEV only: a dev token path (or an already-set stub), no OIDC round-trip.
    if (hasDevToken()) {
      set({ bootstrapped: true });
      return;
    }
    // A session token stored by v0.1.7 or older. Nothing reads it any more
    // (the Bearer lives in memory), so drop it from the origin's storage.
    try {
      localStorage.removeItem("aether:token");
    } catch {
      // ignore
    }
    const session = await fetchOidcSession();
    setSessionBearer(session?.access_token);
    if (session) {
      set({
        user: {
          id: session.sub,
          email: session.email ?? "",
          role: roleFromClaims(session.roles),
          organizationId: "",
          organizationSlug: "",
        },
        bootstrapped: true,
      });
    } else {
      set({ user: null, bootstrapped: true });
    }
  },
  logout: (opts = {}) => {
    // Capture the dev-vs-OIDC path BEFORE clearing tokens: hasDevToken() reads
    // the very `aether:token` we are about to remove, so checking it afterwards
    // always returns false (the original bug — every logout took the OIDC
    // redirect branch, even the dev stub, even on a 401). (W1)
    const wasDev = hasDevToken();
    try {
      localStorage.removeItem("aether:session");
      localStorage.removeItem("aether:token");
    } catch {
      // ignore
    }
    setSessionBearer(undefined);
    set({ user: null });
    // 401 path: the session is already gone server-side. Clear local state only;
    // handleUnauthorized() performs the single /login navigation. Firing the
    // RP-initiated logout here would race it and, under a fan-out of concurrent
    // 401s, trigger N fleet-wide logout redirects. (W1)
    if (opts.reason === "unauthorized") return;
    if (wasDev) {
      // Dev stub / no-OIDC: best-effort local server teardown, stay in the SPA.
      void oidcLogout();
    } else {
      // Real OIDC "Sign out": RP-Initiated Logout — full-page redirect so
      // identity ends the IdP session (fleet-wide), then returns to the app root.
      oidcLogoutRedirect();
    }
  },
}));

/**
 * App-bootstrap hook: kicks off the one-time OIDC session resolution on mount.
 * Mounted once near the app root; gates first paint of guarded routes.
 */
export function useAuthBootstrap(): boolean {
  const bootstrapped = useAuthStore((s) => s.bootstrapped);
  const bootstrap = useAuthStore((s) => s.bootstrap);
  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);
  return bootstrapped;
}

export function useAuth() {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const signInStub = useAuthStore((s) => s.signInStub);
  return { user, logout, signInStub };
}

/**
 * True when the current user holds the `retention:admin` capability. In the
 * current role model `admin` carries every scope; refine to a scope array
 * lookup when the auth store exposes granular OIDC scopes.
 */
export function useIsAdmin(): boolean {
  return useAuthStore((s) => s.user?.role === "admin");
}

/** Route guard: redirects to /login when unauthenticated. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const location = useLocation();
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return <>{children}</>;
}

/**
 * Where /login sends an operator who is already signed in: back to the page
 * RequireAuth bounced them from (its `state.from`), else the app root. Only an
 * in-app absolute path is honoured, and never /login itself.
 */
export function postLoginPath(state: unknown): string {
  const from = (state as { from?: { pathname?: unknown; search?: unknown } } | null | undefined)?.from;
  const path = from?.pathname;
  if (typeof path !== "string" || !path.startsWith("/") || path.startsWith("//") || path === "/login") {
    return "/";
  }
  return path + (typeof from?.search === "string" ? from.search : "");
}

/** Capability guard: renders fallback when the user lacks the role. */
export function RequireRole({
  role,
  children,
  fallback = null,
}: {
  role: Role;
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const user = useAuthStore((s) => s.user);
  const order: Record<Role, number> = { viewer: 0, client: 1, admin: 2 };
  if (!user || order[user.role] < order[role]) return <>{fallback}</>;
  return <>{children}</>;
}
